<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 overflow-y-auto"
    aria-labelledby="modal-title"
    role="dialog"
    aria-modal="true">
    <!-- Background overlay -->
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        aria-hidden="true"
        @click="closeModal" />

      <!-- Modal panel -->
      <div
        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform
          transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
        <!-- Header -->
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="flex items-center justify-between mb-4">
            <h3
              id="modal-title"
              class="text-2xl leading-6 font-bold text-gray-900">
              GBP Checklist - {{ locationName }}
            </h3>
            <div class="flex items-center space-x-4">
              <span class="text-sm text-gray-500">
                Completion: <span class="font-bold">{{ completionPercentage }}%</span>
              </span>
              <button
                type="button"
                class="text-gray-400 hover:text-gray-500"
                @click="closeModal">
                <i class="fas fa-times text-xl" />
              </button>
            </div>
          </div>

          <!-- Tab Navigation -->
          <div class="border-b border-gray-200 mb-6">
            <nav
              class="-mb-px flex space-x-8"
              aria-label="Tabs">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                :class="[
                  currentTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                  'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'
                ]"
                @click="currentTab = tab.id">
                <i
                  :class="tab.icon"
                  class="mr-2" />
                {{ tab.name }}
                <span
                  v-if="tab.count"
                  class="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                  {{ tab.count }}
                </span>
              </button>
            </nav>
          </div>

          <!-- Tab Content -->
          <div class="mt-4">
            <!-- Basic Information Tab -->
            <div
              v-show="currentTab === 'basic'"
              class="space-y-4">
              <GbpChecklistForm
                v-model="checklistData"
                :locations="locations"
                @prefill="prefillFromWebsite" />
            </div>

            <!-- Checklist Items Tab -->
            <div
              v-show="currentTab === 'checklist'"
              class="space-y-4">
              <GbpChecklistItems
                v-model="checklistData.checklist_items"
                :business-name="checklistData.business_name"
                :website-url="checklistData.website_url"
                :maps-url="checklistData.maps_url"
                @update:business-name="checklistData.business_name = $event"
                @update:website-url="checklistData.website_url = $event"
                @update:maps-url="checklistData.maps_url = $event" />
            </div>

            <!-- Recommendations Tab -->
            <div
              v-show="currentTab === 'recommendations'"
              class="space-y-4">
              <GbpRecommendations
                v-model="checklistData.recommendations" />
            </div>

            <!-- Q&A Tab -->
            <div
              v-show="currentTab === 'qa'"
              class="space-y-4">
              <GbpQaSection
                v-model="checklistData.qa_items" />
            </div>

            <!-- Services Tab -->
            <div
              v-show="currentTab === 'services'"
              class="space-y-4">
              <GbpServicesSection
                v-model="checklistData.services_data" />
            </div>

            <!-- Export Tab -->
            <div
              v-show="currentTab === 'export'"
              class="space-y-4">
              <GbpExportSection
                v-model="checklistData.status"
                :checklist-id="checklistId"
                @export="exportPdf" />
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            :disabled="isSaving"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2
              bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2
              focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50
              disabled:cursor-not-allowed"
            @click="saveChecklist">
            <i
              v-if="isSaving"
              class="fas fa-spinner fa-spin mr-2" />
            {{ isSaving ? 'Saving...' : 'Save Checklist' }}
          </button>
          <button
            type="button"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2
              bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2
              focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            @click="closeModal">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, computed, watch, onMounted
} from 'vue'
import axios from 'axios'
import GbpChecklistForm from './gbp_checklist_form.vue'
import GbpChecklistItems from './gbp_checklist_items.vue'
import GbpRecommendations from './gbp_recommendations.vue'
import GbpQaSection from './gbp_qa_section.vue'
import GbpServicesSection from './gbp_services_section.vue'
import GbpExportSection from './gbp_export_section.vue'

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  organizationId: {
    type: String,
    required: true
  },
  locationId: {
    type: [String, Number],
    default: null
  },
  locationName: {
    type: String,
    default: 'Unknown Location'
  },
  locations: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'saved'])

// State
const currentTab = ref('basic')
const isSaving = ref(false)
const isLoading = ref(false)
const checklistId = ref(null)
const checklistData = ref({
  location_id: props.locationId,
  website: '',
  description: '',
  category: '',
  keywords: [],
  avoid_keywords: [],
  business_name: '',
  website_url: '',
  maps_url: '',
  status: 'email_only',
  checklist_items: {},
  recommendations: {},
  services_data: [],
  qa_items: []
})

// Computed
const completionPercentage = computed(() => {
  const items = checklistData.value.checklist_items
  const totalItems = 9 // 9 checkbox items
  const textFields = 3 // business_name, website_url, maps_url

  let completed = 0
  const total = totalItems + textFields

  // Count answered items (both Yes and No count as progress)
  Object.values(items).forEach((value) => {
    if (value === true || value === 'true' || value === false || value === 'false') {
      completed += 1
    }
  })

  // Count filled text fields
  if (checklistData.value.business_name) completed += 1
  if (checklistData.value.website_url) completed += 1
  if (checklistData.value.maps_url) completed += 1

  return total > 0 ? Math.round((completed / total) * 100) : 0
})

const checklistItemsCount = computed(() => Object.values(checklistData.value.checklist_items)
  .filter((v) => v === true || v === 'true' || v === false || v === 'false').length)

const recommendationsCount = computed(() => Object.values(checklistData.value.recommendations)
  .filter((v) => v === true || v === 'true' || v === false || v === 'false').length)

// Tabs configuration
const tabs = computed(() => [
  { id: 'basic', name: 'Basic Info', icon: 'fas fa-info-circle' },
  {
    id: 'checklist', name: 'Checklist', icon: 'fas fa-check-square', count: checklistItemsCount.value
  },
  {
    id: 'recommendations', name: 'Recommendations', icon: 'fas fa-lightbulb', count: recommendationsCount.value
  },
  {
    id: 'qa', name: 'Q&A', icon: 'fas fa-question-circle', count: checklistData.value.qa_items.length
  },
  {
    id: 'services', name: 'Services', icon: 'fas fa-concierge-bell', count: checklistData.value.services_data.length
  },
  { id: 'export', name: 'Export', icon: 'fas fa-file-pdf' }
])

// Methods
const loadChecklist = async() => {
  if (!props.locationId) return

  isLoading.value = true
  try {
    const response = await axios.get(`/ajax/locations/${props.locationId}/gbp_checklist`)
    if (response.data.success && response.data.checklist) {
      const checklist = response.data.checklist
      checklistId.value = checklist.id

      // Map the camelCase response fields to our internal snake_case structure
      // Also convert camelCase recommendation keys back to snake_case
      const convertedRecommendations = {}
      if (checklist.recommendations) {
        Object.entries(checklist.recommendations).forEach(([key, value]) => {
          // Convert camelCase to snake_case
          const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`).replace(/^_/, '')
          convertedRecommendations[snakeKey] = value
        })
      }

      checklistData.value = {
        location_id: checklist.locationId || props.locationId,
        website: checklist.website || '',
        description: checklist.description || '',
        category: checklist.category || '',
        keywords: checklist.keywords || [],
        avoid_keywords: checklist.avoidKeywords || [],
        business_name: checklist.businessName || '',
        website_url: checklist.websiteUrl || '',
        maps_url: checklist.mapsUrl || '',
        status: checklist.status || 'email_only',
        checklist_items: checklist.checklistItems || {},
        recommendations: convertedRecommendations,
        services_data: checklist.servicesData || [],
        qa_items: checklist.qaItems || []
      }
    }
  } catch (error) {
    if (error.response?.status !== 404) {
      // Failed to load checklist - silently handle
    }
  } finally {
    isLoading.value = false
  }
}

const saveChecklist = async() => {
  isSaving.value = true
  try {
    const url = checklistId.value ?
      `/ajax/gbp_checklists/${checklistId.value}` :
      '/ajax/gbp_checklists'

    const method = checklistId.value ? 'put' : 'post'

    // Extract only the fields that should be sent to the server
    const checklistParams = {
      website: checklistData.value.website,
      description: checklistData.value.description,
      category: checklistData.value.category,
      keywords: checklistData.value.keywords || [],
      avoid_keywords: checklistData.value.avoid_keywords || [],
      business_name: checklistData.value.business_name || '',
      website_url: checklistData.value.website_url || '',
      maps_url: checklistData.value.maps_url || '',
      status: checklistData.value.status || 'email_only',
      checklist_items: checklistData.value.checklist_items || {},
      recommendations: checklistData.value.recommendations || {},
      services_data: checklistData.value.services_data || [],
      qa_items: checklistData.value.qa_items || []
    }

    const payload = {
      gbp_checklist: checklistParams,
      location_id: checklistData.value.location_id
    }

    const response = await axios[method](url, payload)

    if (response.data.success) {
      checklistId.value = response.data.checklist.id
      emit('saved', response.data.checklist)

      // Checklist saved successfully
    }
  } catch (error) {
    // Failed to save checklist - handle error silently
  } finally {
    isSaving.value = false
  }
}

const prefillFromWebsite = async() => {
  if (!checklistData.value.location_id) {
    // Location required for prefill
    return
  }

  try {
    // Extract only the fields that should be sent to the server
    const checklistParams = {
      website: checklistData.value.website,
      description: checklistData.value.description,
      category: checklistData.value.category,
      keywords: checklistData.value.keywords || [],
      avoid_keywords: checklistData.value.avoid_keywords || [],
      business_name: checklistData.value.business_name || '',
      website_url: checklistData.value.website_url || '',
      maps_url: checklistData.value.maps_url || '',
      status: checklistData.value.status || 'email_only',
      checklist_items: checklistData.value.checklist_items || {},
      recommendations: checklistData.value.recommendations || {},
      services_data: checklistData.value.services_data || [],
      qa_items: checklistData.value.qa_items || []
    }

    const response = await axios.post('/ajax/gbp_checklists', {
      gbp_checklist: checklistParams,
      location_id: checklistData.value.location_id,
      prefill: 'true'
    })

    if (response.data.success) {
      checklistData.value = {
        ...checklistData.value,
        ...response.data.checklist
      }
      // Data prefilled successfully
    }
  } catch (error) {
    // Failed to prefill data - handle silently
  }
}

const exportPdf = async() => {
  if (!checklistId.value) {
    // Checklist must be saved before export
    return
  }

  window.open(`/ajax/gbp_checklists/${checklistId.value}/export`, '_blank')
}

const closeModal = () => {
  emit('close')
}

// Method to reset the checklist data
const resetChecklistData = () => {
  checklistId.value = null
  checklistData.value = {
    location_id: props.locationId,
    website: '',
    description: '',
    category: '',
    keywords: [],
    avoid_keywords: [],
    business_name: '',
    website_url: '',
    maps_url: '',
    status: 'email_only',
    checklist_items: {},
    recommendations: {},
    services_data: [],
    qa_items: []
  }
}

// Watchers
watch(() => props.locationId, (newValue) => {
  if (newValue) {
    resetChecklistData()
    checklistData.value.location_id = newValue
    loadChecklist()
  }
})

// Lifecycle
onMounted(() => {
  if (props.locationId) {
    loadChecklist()
  }
})
</script>

<style scoped>
/* Additional styles if needed */
</style>
